import React, { useState, useEffect } from 'react';

import { Button, Typography, useMediaQuery } from '@mui/material';
import copyToClipboard from 'copy-to-clipboard';
import Link from 'next/link';
import { useRouter } from 'next/router';
import QRCode from 'react-qr-code';
import { useAccount } from 'wagmi';

import { getFileAPI } from '@/api/getFile';
import Wrapper from '@/components/BodyLayout/Wrapper';
import { QrPopup } from '@/components/Popup/QrPopup';
import useQrVisible from '@/hooks/useQrVisible';
import { GetFileResponse } from 'interfaces/ApiType/apiType';
import CopyIcon from 'public/assets/copy-white.svg';
import ExpandIcon from 'public/assets/expand.svg';
import HomeIcon from 'public/assets/home2.svg';

import { isBrowser } from '../../constants';
import { isTokenValid } from '../../utils';
import PreviewImg from './PreviewImg';

export default function FinalUpload({
  fileResponse,
}: {
  fileResponse: GetFileResponse;
}) {
  const [fileData, setFileData] = useState<GetFileResponse>(null);
  const router = useRouter();
  const fileId = router.query.fileId as string;
  const currentURL =
    isBrowser && fileResponse
      ? `${window.location.origin}/files/${fileId}`
      : '';
  const [copyURLVal, setURLCopyVal] = useState<string>('Copy');
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const isMediaMax1025px = useMediaQuery('(max-width : 1025px)');
  const { qrRef, isQrVisible, setIsQrVisible } = useQrVisible(false);
  const { data: signer } = useAccount();

  const copyURLAnimation = () => {
    setURLCopyVal('Copied');
    setTimeout(() => {
      setURLCopyVal('Copy');
    }, 500);
  };

  const getFileData = async () => {
    try {
      const file = await getFileAPI({ fileId });
      if (file) setFileData(file);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    getFileData();
  }, []);

  useEffect(() => {
    setIsConnected(
      isTokenValid(localStorage.getItem('token'), signer?.address)
    );
  }, [signer?.address]);

  const QR = () => {
    return (
      <div ref={qrRef} className="relative">
        <div className="flex-col w-full h-full items-center justify-center">
          <div className="flex w-full p-2 my-3 justify-center">
            <div className="border-2 p-2 rounded-md">
              <QRCode
                size={115}
                value={
                  `${process.env.NEXT_PUBLIC_ROOT_URL}/files/${fileId}` ||
                  `${process.env.NEXT_PUBLIC_ROOT_URL}/`
                }
              />
            </div>
          </div>
          <div className="flex w-full p-2 justify-center">
            Scan QR Code from Mobile
          </div>
        </div>
      </div>
    );
  };

  return (
    <Wrapper>
      <div
        className={`bg-white rounded-lg py-8 m-auto text-center flex  items-center relative
         ${
           isMediaMax1025px ? 'px-8 w-full flex-col' : 'w-[50%] px-16 flex-row'
         } `}
      >
        <Link
          href={
            isConnected
              ? '/files'
              : `/files/${fileResponse.data.uuid}?ownerFirstVisit=true`
          }
          passHref={true}
        >
          <a target="_blank">
            <div className="bg-yellow p-3 rounded-md absolute right-4 top-4 cursor-pointer">
              <img src={HomeIcon.src} alt="Home" />
            </div>
          </a>
        </Link>
        <div
          className={` h-full ${
            isMediaMax1025px ? 'w-full mb-6' : 'mr-12 w-5/12'
          }`}
        >
          <PreviewImg fileResponse={fileResponse} />
        </div>
        <div
          className={`flex flex-col ${isMediaMax1025px ? 'w-full' : ' w-7/12'}`}
        >
          <div className="font-bold text-3xl mb-4">Share Away!</div>
          <div className="block font-semibold w-full text-sm mb-4">
            {isConnected
              ? 'Share this link with the world! Only people that meet your rules will be able to access the file.'
              : 'Your file is now encrypted and stored on IPFS. Only people with the link can now access it.'}
          </div>
          <div className="w-full flex flex-row justify-start mt-4 mb-1 font-semibold">
            {/* <div>Shareable URL: </div> */}
            <div
              onClick={() => setIsQrVisible(true)}
              className="cursor-pointer"
            >
              Open QR Code
            </div>
          </div>
          <div className="w-full mb-4 relative flex border rounded-md">
            <input
              readOnly
              defaultValue={currentURL}
              className="p-3 text-gray-500 caret-transparent w-[73%] rounded-md"
            />

            <Button
              variant="text"
              onClick={() => {
                copyToClipboard(currentURL);
                copyURLAnimation();
              }}
              className="absolute bg-black normal-case"
              style={{
                textTransform: 'none',
                bottom: '6px',
                right: '10px',
              }}
              disableElevation={true}
            >
              <Typography className="text-white flex ">
                <img src={CopyIcon.src} alt="Copy" />
                <span className="ml-2 font-thin">{copyURLVal}</span>
              </Typography>
            </Button>
          </div>
          {isConnected &&
            fileData &&
            fileData.data.token &&
            fileData.data.token.explorerLink && (
              <a
                href={fileData.data.token.explorerLink}
                target="_blank"
                rel="noreferrer"
              >
                <div className="flex flex-row items-center justify-center w-full">
                  <div className="cursor-pointer font-semibold mr-2">
                    Check Explorer
                  </div>
                  <img src={ExpandIcon.src} alt="Check Explorer" />
                </div>
              </a>
            )}
        </div>
        <QrPopup
          isOpen={isQrVisible}
          width={isMediaMax1025px ? '90%' : '370px'}
          margin={isMediaMax1025px ? '5%' : '10% auto'}
          closePopup={() => {}}
        >
          <div className="overflow-auto max-h-full max-w-full box-border">
            {QR()}
          </div>
        </QrPopup>
      </div>
    </Wrapper>
  );
}
